import { useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Stack, Typography,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { Device } from '../../services/devicesApiService';

const schema = z.object({
  nickname: z.string().max(200, 'Nickname must be 200 characters or fewer'),
});

type FormData = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onClose: () => void;
  device: Device | null;
  onSubmit: (nickname: string) => Promise<void>;
  isLoading?: boolean;
}

export function DeviceNicknameDialog({ open, onClose, device, onSubmit, isLoading }: Props) {
  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { nickname: '' },
  });

  useEffect(() => {
    if (open) reset({ nickname: device?.nickname ?? '' });
  }, [open, device, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Device Nickname</DialogTitle>
      <form onSubmit={handleSubmit((data) => onSubmit(data.nickname))}>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              UID: {device?.deviceUid}
            </Typography>
            <Controller
              name="nickname"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Nickname"
                  fullWidth
                  placeholder="e.g. Lab Bench A"
                  error={!!errors.nickname}
                  helperText={errors.nickname?.message ?? 'Leave empty to clear the nickname'}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
